import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type {
  BackgroundJobDetail,
  BackgroundJobPage,
  BackgroundJobState,
  BackgroundQueue,
  BackgroundWorkOverview,
  RetryAllResult,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../../../lib/api-client';
import { MESSAGE_LOG_KEY } from '../../notifications/api/notifications-api';
import { ADMIN_MENU_COUNTS_KEY } from './overview-api';

// ADMIN Background work (Admin redesign batch 2, Option A): our own page over the BullMQ queues.

export const BACKGROUND_WORK_KEY = ['admin', 'background-work'] as const;

/** Jobs move quickly; the page keeps itself current while it is open, and never blanks what it shows. */
const REFRESH_MS = 10_000;

export function useBackgroundWork(enabled: boolean): UseQueryResult<BackgroundWorkOverview, ApiError> {
  return useQuery<BackgroundWorkOverview, ApiError>({
    queryKey: [...BACKGROUND_WORK_KEY, 'overview'],
    queryFn: () => apiGet<BackgroundWorkOverview>('/admin/jobs'),
    placeholderData: (previous) => previous,
    refetchInterval: REFRESH_MS,
    enabled,
  });
}

export function useBackgroundJobs(queue: BackgroundQueue, state: BackgroundJobState, page: number, enabled: boolean): UseQueryResult<BackgroundJobPage, ApiError> {
  return useQuery<BackgroundJobPage, ApiError>({
    queryKey: [...BACKGROUND_WORK_KEY, 'jobs', queue, state, page],
    queryFn: () => apiGet<BackgroundJobPage>(`/admin/jobs/${queue}/jobs`, { query: { state, page } }),
    placeholderData: (previous) => previous,
    refetchInterval: REFRESH_MS,
    enabled,
    retry: false,
  });
}

export function useBackgroundJob(queue: BackgroundQueue, id: string | undefined, enabled: boolean): UseQueryResult<BackgroundJobDetail, ApiError> {
  return useQuery<BackgroundJobDetail, ApiError>({
    queryKey: [...BACKGROUND_WORK_KEY, 'job', queue, id ?? null],
    queryFn: () => apiGet<BackgroundJobDetail>(`/admin/jobs/${queue}/jobs/${encodeURIComponent(id ?? '')}`),
    enabled: enabled && id !== undefined,
    retry: false,
  });
}

type Snapshot = readonly (readonly [QueryKey, unknown])[];

/** Act first: a retried or removed job leaves the failed list at once; a refusal puts it back. */
async function dropJob(queryClient: QueryClient, queue: BackgroundQueue, id: string): Promise<Snapshot> {
  const listKey = [...BACKGROUND_WORK_KEY, 'jobs', queue, 'failed'];
  await queryClient.cancelQueries({ queryKey: listKey });
  const snapshot = queryClient.getQueriesData({ queryKey: listKey });
  queryClient.setQueriesData<BackgroundJobPage>({ queryKey: listKey }, (page) =>
    page ? { ...page, rows: page.rows.filter((row) => row.id !== id), total: Math.max(0, page.total - 1) } : page,
  );
  return snapshot;
}

function restore(queryClient: QueryClient, snapshot: Snapshot | undefined): void {
  for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data);
}

/** A retried email job is a Send again on Email delivery too, and either changes the red counts. */
async function refresh(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: BACKGROUND_WORK_KEY }),
    queryClient.invalidateQueries({ queryKey: MESSAGE_LOG_KEY }),
    queryClient.invalidateQueries({ queryKey: ADMIN_MENU_COUNTS_KEY }),
  ]);
}

export interface JobRef {
  readonly queue: BackgroundQueue;
  readonly id: string;
}

export function useRetryJob() {
  const queryClient = useQueryClient();
  return useMutation<{ retried: boolean }, ApiError, JobRef, Snapshot>({
    mutationFn: (job) => apiPost<{ retried: boolean }>(`/admin/jobs/${job.queue}/jobs/${encodeURIComponent(job.id)}/retry`),
    onMutate: (job) => dropJob(queryClient, job.queue, job.id),
    onError: (_error, _job, snapshot) => restore(queryClient, snapshot),
    onSettled: () => refresh(queryClient),
  });
}

/** Remove: asked beside its button, because a removed job can't be put back. */
export function useRemoveJob() {
  const queryClient = useQueryClient();
  return useMutation<{ removed: boolean }, ApiError, JobRef, Snapshot>({
    mutationFn: (job) => apiDelete<{ removed: boolean }>(`/admin/jobs/${job.queue}/jobs/${encodeURIComponent(job.id)}`),
    onMutate: (job) => dropJob(queryClient, job.queue, job.id),
    onError: (_error, _job, snapshot) => restore(queryClient, snapshot),
    onSettled: () => refresh(queryClient),
  });
}

/** Retry all: the server judges job by job and says what it skipped, so this waits for its answer. */
export function useRetryAll() {
  const queryClient = useQueryClient();
  return useMutation<RetryAllResult, ApiError, BackgroundQueue>({
    mutationFn: (queue) => apiPost<RetryAllResult>(`/admin/jobs/${queue}/retry-all`),
    onSettled: () => refresh(queryClient),
  });
}
